
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useSimpleProfile } from '@/hooks/useSimpleProfile';
import { PrivacySettings } from '@/types/profile';

const UserPrivacySettings = () => {
  const { profile, isLoading, isUpdating, updateProfile } = useSimpleProfile();
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
    profile_visibility: 'public',
    email_visibility: 'private',
    activity_visibility: 'friends',
    show_email: false,
    show_phone: false,
    show_activity: true,
  });

  useEffect(() => {
    if (profile?.privacy_settings) {
      setPrivacySettings(profile.privacy_settings as PrivacySettings);
    }
  }, [profile]);

  const handleSave = () => {
    updateProfile({
      privacy_settings: privacySettings
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Privacy Settings</CardTitle>
          <CardDescription>Control who can see your information and activity.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="h-10 bg-gray-200 rounded animate-pulse w-1/2"></div>
            <div className="h-10 bg-gray-200 rounded animate-pulse w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Privacy Settings</CardTitle>
        <CardDescription>Control who can see your information and activity.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="profile_visibility">Profile Visibility</Label>
            <Select 
              value={privacySettings.profile_visibility} 
              onValueChange={(value) => setPrivacySettings(prev => ({ ...prev, profile_visibility: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select profile visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="friends">Friends Only</SelectItem>
                <SelectItem value="private">Private</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email_visibility">Email Visibility</Label>
            <Select 
              value={privacySettings.email_visibility} 
              onValueChange={(value) => setPrivacySettings(prev => ({ ...prev, email_visibility: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select email visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="friends">Friends Only</SelectItem>
                <SelectItem value="private">Private</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="show_email">Show Email in Profile</Label>
              <p className="text-sm text-muted-foreground">
                Display your email address on your public profile.
              </p>
            </div>
            <Switch
              id="show_email"
              checked={privacySettings.show_email}
              onCheckedChange={(checked) => setPrivacySettings(prev => ({ ...prev, show_email: checked }))}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="show_phone">Show Phone in Profile</Label>
              <p className="text-sm text-muted-foreground">
                Display your phone number on your public profile.
              </p>
            </div>
            <Switch
              id="show_phone"
              checked={privacySettings.show_phone}
              onCheckedChange={(checked) => setPrivacySettings(prev => ({ ...prev, show_phone: checked }))}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="show_activity">Show Activity</Label>
              <p className="text-sm text-muted-foreground">
                Show your recent activity and purchases.
              </p>
            </div>
            <Switch
              id="show_activity"
              checked={privacySettings.show_activity}
              onCheckedChange={(checked) => setPrivacySettings(prev => ({ ...prev, show_activity: checked }))}
            />
          </div>
        </div>

        <Button onClick={handleSave} disabled={isUpdating}>
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default UserPrivacySettings;
